import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

interface DataPoint {
  label: string
  value: number
  category?: string
  color?: string
}

interface BarChartProps {
  data: DataPoint[]
  width?: number
  height?: number
  margin?: { top: number; right: number; bottom: number; left: number }
  title?: string
  xLabel?: string
  yLabel?: string
  colors?: string[]
  horizontal?: boolean
  grouped?: boolean
  stacked?: boolean
  showValues?: boolean
  animate?: boolean
  onBarClick?: (data: DataPoint) => void
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  width = 800,
  height = 400,
  margin = { top: 40, right: 30, bottom: 80, left: 70 },
  title,
  xLabel,
  yLabel,
  colors = ['#4A90E2', '#50C878', '#FF6B35', '#7B68EE', '#E74C3C'],
  horizontal = false,
  grouped = false,
  stacked = false,
  showValues = true,
  animate = true,
  onBarClick
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!svgRef.current || !data.length) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Get unique categories for grouped/stacked
    const categories = Array.from(new Set(data.map(d => d.category || 'default')))
    const labels = Array.from(new Set(data.map(d => d.label)))

    const colorScale = d3.scaleOrdinal<string>()
      .domain(categories)
      .range(colors)

    if (stacked && categories.length > 1) {
      // Stacked bar chart
      const stackedData = labels.map(label => {
        const obj: any = { label }
        categories.forEach(cat => {
          const item = data.find(d => d.label === label && d.category === cat)
          obj[cat] = item?.value || 0
        })
        return obj
      })

      const stack = d3.stack<any>()
        .keys(categories)

      const series = stack(stackedData)

      const xScale = d3.scaleBand()
        .domain(labels)
        .range([0, innerWidth])
        .padding(0.3)

      const yScale = d3.scaleLinear()
        .domain([0, d3.max(series, s => d3.max(s, d => d[1])) as number * 1.1])
        .nice()
        .range([innerHeight, 0])

      // Bars
      g.selectAll('g.series')
        .data(series)
        .enter()
        .append('g')
        .attr('class', 'series')
        .attr('fill', d => colorScale(d.key))
        .selectAll('rect')
        .data(d => d)
        .enter()
        .append('rect')
        .attr('x', d => xScale(d.data.label) || 0)
        .attr('width', xScale.bandwidth())
        .attr('y', innerHeight)
        .attr('height', 0)
        .attr('rx', 4)
        .transition()
        .duration(animate ? 800 : 0)
        .delay((_, i) => animate ? i * 50 : 0)
        .attr('y', d => yScale(d[1]))
        .attr('height', d => yScale(d[0]) - yScale(d[1]))

      // Axes
      g.append('g')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(xScale))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end')

      g.append('g')
        .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => d3.format('.2s')(d as number)))

    } else if (grouped && categories.length > 1) {
      // Grouped bar chart
      const x0 = d3.scaleBand()
        .domain(labels)
        .range([0, innerWidth])
        .padding(0.2)

      const x1 = d3.scaleBand()
        .domain(categories)
        .range([0, x0.bandwidth()])
        .padding(0.05)

      const yScale = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.value) as number * 1.1])
        .nice()
        .range([innerHeight, 0])

      const labelGroups = g.selectAll('g.label-group')
        .data(labels)
        .enter()
        .append('g')
        .attr('class', 'label-group')
        .attr('transform', d => `translate(${x0(d)},0)`)

      labelGroups.selectAll('rect')
        .data(label => categories.map(cat => {
          const item = data.find(d => d.label === label && d.category === cat)
          return { label, category: cat, value: item?.value || 0 }
        }))
        .enter()
        .append('rect')
        .attr('x', d => x1(d.category) || 0)
        .attr('width', x1.bandwidth())
        .attr('y', innerHeight)
        .attr('height', 0)
        .attr('fill', d => colorScale(d.category))
        .attr('rx', 3)
        .style('cursor', onBarClick ? 'pointer' : 'default')
        .on('click', (_, d) => onBarClick?.(d as any))
        .transition()
        .duration(animate ? 800 : 0)
        .delay((_, i) => animate ? i * 100 : 0)
        .attr('y', d => yScale(d.value))
        .attr('height', d => innerHeight - yScale(d.value))

      // Axes
      g.append('g')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x0))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end')

      g.append('g')
        .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => d3.format('.2s')(d as number)))

    } else {
      // Simple bar chart
      const xScale = horizontal
        ? d3.scaleLinear()
            .domain([0, d3.max(data, d => d.value) as number * 1.1])
            .nice()
            .range([0, innerWidth])
        : d3.scaleBand<string>()
            .domain(data.map(d => d.label))
            .range([0, innerWidth])
            .padding(0.3)

      const yScale = horizontal
        ? d3.scaleBand<string>()
            .domain(data.map(d => d.label))
            .range([0, innerHeight])
            .padding(0.3)
        : d3.scaleLinear()
            .domain([0, d3.max(data, d => d.value) as number * 1.1])
            .nice()
            .range([innerHeight, 0])

      // Bars
      const bars = g.selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('fill', (d, i) => d.color || colors[i % colors.length])
        .attr('rx', 6)
        .style('cursor', onBarClick ? 'pointer' : 'default')

      if (horizontal) {
        bars
          .attr('x', 0)
          .attr('y', d => (yScale as d3.ScaleBand<string>)(d.label) || 0)
          .attr('height', (yScale as d3.ScaleBand<string>).bandwidth())
          .attr('width', 0)
          .transition()
          .duration(animate ? 800 : 0)
          .delay((_, i) => animate ? i * 100 : 0)
          .attr('width', d => (xScale as d3.ScaleLinear<number, number>)(d.value))
      } else {
        bars
          .attr('x', d => (xScale as d3.ScaleBand<string>)(d.label) || 0)
          .attr('y', innerHeight)
          .attr('width', (xScale as d3.ScaleBand<string>).bandwidth())
          .attr('height', 0)
          .transition()
          .duration(animate ? 800 : 0)
          .delay((_, i) => animate ? i * 100 : 0)
          .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.value))
          .attr('height', d => innerHeight - (yScale as d3.ScaleLinear<number, number>)(d.value))
      }

      // Click handler
      bars.on('click', (_, d) => onBarClick?.(d))

      // Tooltip
      if (tooltipRef.current) {
        const tooltip = d3.select(tooltipRef.current)
        bars
          .on('mouseover', function(event, d) {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('opacity', 0.8)

            tooltip
              .style('opacity', 1)
              .style('left', `${event.pageX + 10}px`)
              .style('top', `${event.pageY - 10}px`)
              .html(`
                <strong>${d.label}</strong><br/>
                Value: ${d.value.toLocaleString()}
              `)
          })
          .on('mouseout', function() {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('opacity', 1)

            tooltip.style('opacity', 0)
          })
      }

      // Value labels
      if (showValues) {
        const valueLabels = g.selectAll('.value-label')
          .data(data)
          .enter()
          .append('text')
          .attr('class', 'value-label')
          .attr('text-anchor', 'middle')
          .style('font-size', '11px')
          .style('font-weight', '600')
          .style('fill', '#374151')
          .style('opacity', 0)

        if (horizontal) {
          valueLabels
            .attr('x', d => (xScale as d3.ScaleLinear<number, number>)(d.value) + 5)
            .attr('y', d => ((yScale as d3.ScaleBand<string>)(d.label) || 0) + (yScale as d3.ScaleBand<string>).bandwidth() / 2 + 4)
            .attr('text-anchor', 'start')
            .text(d => d3.format('.2s')(d.value))
        } else {
          valueLabels
            .attr('x', d => ((xScale as d3.ScaleBand<string>)(d.label) || 0) + (xScale as d3.ScaleBand<string>).bandwidth() / 2)
            .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.value) - 8)
            .text(d => d3.format('.2s')(d.value))
        }

        valueLabels
          .transition()
          .delay(animate ? 800 : 0)
          .duration(300)
          .style('opacity', 1)
      }

      // Axes
      if (horizontal) {
        g.append('g')
          .attr('transform', `translate(0,${innerHeight})`)
          .call(d3.axisBottom(xScale as d3.ScaleLinear<number, number>)
            .ticks(5)
            .tickFormat(d => d3.format('.2s')(d as number))
          )

        g.append('g')
          .call(d3.axisLeft(yScale as d3.ScaleBand<string>))
      } else {
        g.append('g')
          .attr('transform', `translate(0,${innerHeight})`)
          .call(d3.axisBottom(xScale as d3.ScaleBand<string>))
          .selectAll('text')
          .attr('transform', 'rotate(-45)')
          .style('text-anchor', 'end')

        g.append('g')
          .call(d3.axisLeft(yScale as d3.ScaleLinear<number, number>)
            .ticks(5)
            .tickFormat(d => d3.format('.2s')(d as number))
          )
      }
    }

    // Labels
    if (xLabel) {
      svg.append('text')
        .attr('x', margin.left + innerWidth / 2)
        .attr('y', height - 10)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#6B7280')
        .text(xLabel)
    }

    if (yLabel) {
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -(margin.top + innerHeight / 2))
        .attr('y', 15)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#6B7280')
        .text(yLabel)
    }

    if (title) {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', 25)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', '600')
        .style('fill', '#1F2937')
        .text(title)
    }

    // Legend (for grouped/stacked)
    if ((grouped || stacked) && categories.length > 1) {
      const legend = svg.append('g')
        .attr('transform', `translate(${width - margin.right - 100}, ${margin.top})`)

      categories.forEach((cat, i) => {
        const legendItem = legend.append('g')
          .attr('transform', `translate(0, ${i * 22})`)

        legendItem.append('rect')
          .attr('width', 16)
          .attr('height', 16)
          .attr('rx', 3)
          .attr('fill', colorScale(cat))

        legendItem.append('text')
          .attr('x', 22)
          .attr('y', 12)
          .style('font-size', '11px')
          .style('fill', '#4B5563')
          .text(cat)
      })
    }

  }, [data, width, height, margin, title, xLabel, yLabel, colors, horizontal, grouped, stacked, showValues, animate, onBarClick])

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={svgRef} width={width} height={height} />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          opacity: 0,
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '10px 14px',
          fontSize: '13px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
          zIndex: 1000,
          transition: 'opacity 0.2s'
        }}
      />
    </div>
  )
}

export default BarChart
